import { Link } from 'react-router';
import { api } from '../../shared/api/client';
import { Card } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

type Dashboard = {
  pastoral: null | {
    members: number; requests: number; visitors: { newThisWeek: number; inFollowUp: number }; counseling: { waiting: number; scheduled: number };
    baptisms: number; maintenance: { open: number; urgent: number }; lastService: { title: string; startsAt: string; attendance: number | null } | null;
  };
  leader: { id: string; name: string; members: number; cells: number; withoutCell: number; nextMeeting: { id: string; topic: string; startsAt: string } | null }[];
  cells: { id: string; name: string; members: number }[];
};

const when = (iso: string) => new Date(iso).toLocaleString('es', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

function Tile({ to, value, label, warn }: { to: string; value: number | string; label: string; warn?: boolean }) {
  return (
    <Link to={to} className={`stat-tile${warn ? ' stat-tile--warn' : ''}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </Link>
  );
}

/** Tableros de Inicio: Pastoral (lo que pide atención) y líder (su ministerio). */
export function Dashboards({ churchId }: { churchId: string }) {
  const data = useAsync((signal) => api.get<Dashboard>(`/reporting/churches/${churchId}/dashboard`, signal), [churchId]);
  if (data.status !== 'ready') return null;
  const d = data.data;
  return (
    <>
      {d.pastoral ? (
        <Card title="Tablero pastoral">
          <div className="stat-tiles">
            <Tile to="/app/miembros" value={d.pastoral.members} label="miembros activos" />
            <Tile to="/app/miembros" value={d.pastoral.requests} label="solicitudes por aprobar" warn={d.pastoral.requests > 0} />
            <Tile to="/app/visitantes" value={d.pastoral.visitors.newThisWeek} label="visitantes esta semana" />
            <Tile to="/app/visitantes" value={d.pastoral.visitors.inFollowUp} label="visitantes en seguimiento" />
            <Tile to="/app/consejeria?ver=solicitudes" value={d.pastoral.counseling.waiting} label="consejerías sin cita" warn={d.pastoral.counseling.waiting > 0} />
            <Tile to="/app/ordenanzas" value={d.pastoral.baptisms} label="bautismos en proceso" />
            <Tile to="/app/mantenimiento" value={d.pastoral.maintenance.open} label={d.pastoral.maintenance.urgent ? `mantenimiento (${d.pastoral.maintenance.urgent} urgente)` : 'mantenimiento abierto'} warn={d.pastoral.maintenance.urgent > 0} />
            {d.pastoral.lastService ? (
              <Tile to="/app/cultos" value={d.pastoral.lastService.attendance ?? '—'} label={`asistencia · ${d.pastoral.lastService.title}`} />
            ) : null}
          </div>
        </Card>
      ) : null}
      {d.leader.map((m) => (
        <Card key={m.id} title={`Mi ministerio: ${m.name}`}>
          <div className="stat-tiles">
            <Tile to={`/app/ministerios/${m.id}`} value={m.members} label="integrantes" />
            {m.cells ? <Tile to={`/app/ministerios/${m.id}`} value={m.cells} label="células" /> : null}
            {m.cells ? <Tile to={`/app/ministerios/${m.id}`} value={m.withoutCell} label="sin célula" warn={m.withoutCell > 0} /> : null}
          </div>
          <p className="small">
            {m.nextMeeting ? (
              <>Próxima reunión: <Link to={`/app/reuniones/${m.nextMeeting.id}`}>{m.nextMeeting.topic}</Link> · {when(m.nextMeeting.startsAt)}</>
            ) : (
              <>No hay reuniones programadas. <Link to={`/app/ministerios/${m.id}/reuniones/nueva`}>Programar una</Link></>
            )}
          </p>
        </Card>
      ))}
    </>
  );
}
